/*
 * FreeGiftEngine — free Konjac sponge reconciler (ILEM US)
 * ---------------------------------------------------------------------------
 * Replaces the add-only `free2Sponges()` routine that used to live in
 * assets/global.js (ComboOfferCartSync). That routine computed
 *   need = max(0, entitled - inCart)
 * and never computed a surplus, so removing the qualifying product left the
 * sponge in the cart — and if the automatic discount stopped matching, the
 * customer was charged for it.
 *
 * This engine RECONCILES instead of adding. On every cart change it computes
 * the gift lines the cart *should* have, diffs against the ones it *does*
 * have, and issues both adds and removals.
 *
 *   GET /cart.js -> buildPlan(cart, cfg) -> { updates, adds }
 *                   POST /cart/update.js   (removals + quantity changes)
 *                   POST /cart/add.js      (new lines)
 *                   re-render cart sections (without popping the drawer open)
 *
 * buildPlan() is PURE. It takes a cart object and returns a plan. That is what
 * makes this testable with no browser — see the test suite. Do not put I/O,
 * DOM access or `await` inside it.
 *
 * CONVERGENCE IS THE INVARIANT THAT MATTERS: after applying a plan, running
 * buildPlan again on the resulting cart must return an empty plan. If it does
 * not, the engine writes -> fires a cart event -> writes again, forever.
 *
 * CONFIGURATION lives in Theme settings -> "Free gift (Konjac sponge)" and is
 * emitted as JSON by snippets/free-gift-config.liquid. Nothing here is
 * hardcoded to a product.
 *
 * !! THE DISCOUNT MAKES IT FREE, NOT THIS CODE. !!
 * The sponge is added as an ordinary paid line. A Shopify automatic discount
 * zeroes it. The variant selected in theme settings MUST be covered by that
 * discount. Because the theme cannot see the discount, this engine is STRICT:
 * it grants only the configured variant and grants nothing when that variant
 * is unavailable, rather than substituting a colour the discount may not
 * cover. Withholding a gift is recoverable. Charging for one is not.
 */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Property names written onto every gift line.
   * Keys starting with "_" are hidden from the customer and from the order.
   * GIFT_PARENT_LABEL deliberately is not, so warehouse and support can see
   * why a sponge is in the order.
   * ------------------------------------------------------------------- */
  var P_GIFT = '_free_gift';
  var P_PARENT = '_gift_parent_id';
  var P_FLAG = '_Free';
  var P_FLAG_VALUE = '_Konjac_Sponge';
  var GIFT_PARENT_LABEL = 'Free with';

  /* =====================================================================
   * PURE CORE
   * ===================================================================== */

  function toId(value) {
    var n = parseInt(value, 10);
    return isNaN(n) ? null : n;
  }

  function props(item) {
    return item && item.properties && typeof item.properties === 'object' ? item.properties : {};
  }

  /**
   * A line is an engine-owned gift only if it is the gift product AND carries
   * our tag. Scoping to the gift product id matters: without it the engine
   * would delete another app's gift line, or a paid line that happens to
   * carry the same property.
   */
  function isGiftLine(item, cfg) {
    if (!item || toId(item.product_id) !== cfg.giftProductId) return false;
    return String(props(item)[P_GIFT]) === 'true';
  }

  /** Any line of the gift product that we did not create. */
  function isUntaggedGiftProductLine(item, cfg) {
    return item && toId(item.product_id) === cfg.giftProductId && !isGiftLine(item, cfg);
  }

  function giftProperties(parentId, parentTitle) {
    var p = {};
    p[P_GIFT] = 'true';
    p[P_PARENT] = String(parentId);
    p[P_FLAG] = P_FLAG_VALUE;
    if (parentTitle) p[GIFT_PARENT_LABEL] = String(parentTitle);
    return p;
  }

  function emptyPlan() {
    return { updates: {}, adds: [] };
  }

  /**
   * Drop adds for a parent this reconcile cycle has ALREADY added for.
   *
   * The engine verifies its own work by re-reading the cart and re-planning.
   * If that read is stale for any reason, the re-plan would decide the gift is
   * still missing and add it a second time — turning the verification step
   * into the very bug it exists to catch. Removals and quantity corrections
   * are always allowed through; only a repeat ADD for the same parent within
   * one cycle is suppressed.
   *
   * Pure, so it can be tested.
   */
  function withoutRepeatAdds(plan, addedParents) {
    var out = { updates: plan.updates, adds: [] };
    for (var i = 0; i < plan.adds.length; i++) {
      var add = plan.adds[i];
      var parent = add.properties ? add.properties[P_PARENT] : null;
      if (parent && addedParents[parent]) continue;
      out.adds.push(add);
    }
    return out;
  }

  function recordAdds(plan, addedParents) {
    for (var i = 0; i < plan.adds.length; i++) {
      var parent = plan.adds[i].properties ? plan.adds[i].properties[P_PARENT] : null;
      if (parent) addedParents[parent] = true;
    }
  }

  function planIsEmpty(plan) {
    return Object.keys(plan.updates).length === 0 && plan.adds.length === 0;
  }

  /**
   * buildPlan(cart, cfg) -> { updates: { <lineKey>: <qty> }, adds: [ {id, quantity, properties} ] }
   *
   * PURE. No I/O, no DOM, no throwing on malformed input.
   */
  function buildPlan(cart, cfg) {
    var plan = emptyPlan();

    if (!cfg || !cfg.enabled) return plan;
    if (!cfg.giftProductId) return plan;

    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    if (!items.length) return plan;

    /* ---- 1. Entitlement -------------------------------------------------
     * Per qualifying UNIT, per parent PRODUCT. Two units of one product earn
     * two gifts. Two different qualifying products earn separate gift lines,
     * tagged to their own parent — that is what makes "remove one product,
     * only its gifts go" work.
     *
     * A gift line never earns a gift of its own, and the gift product can
     * never qualify (that would feed itself).
     */
    var qualifying = {};
    for (var q = 0; q < cfg.qualifyingProductIds.length; q++) {
      var qid = toId(cfg.qualifyingProductIds[q]);
      if (qid && qid !== cfg.giftProductId) qualifying[qid] = true;
    }

    var entitled = {};      // parentProductId -> gift quantity owed
    var parentTitle = {};   // parentProductId -> product title (for the visible property)
    var i;
    var item;

    for (i = 0; i < items.length; i++) {
      item = items[i];
      var pid = toId(item && item.product_id);
      if (!pid || !qualifying[pid]) continue;
      /* No free line ever earns a gift — ours or another app's. Scoping this
       * to our own gift product would let a third-party "free X" promotion
       * mint sponges. Over-granting costs money; withholding does not. */
      if (String(props(item)[P_GIFT]) === 'true') continue;

      var qty = toId(item.quantity) || 0;
      if (qty <= 0) continue;

      entitled[pid] = (entitled[pid] || 0) + qty * cfg.perUnit;
      if (!parentTitle[pid]) {
        parentTitle[pid] = item.product_title || item.title || '';
      }
    }

    /* Optional safety cap on the whole cart. 0 means unlimited. */
    if (cfg.maxPerCart > 0) {
      var budget = cfg.maxPerCart;
      var pidsInOrder = Object.keys(entitled);
      for (i = 0; i < pidsInOrder.length; i++) {
        var allowed = Math.min(entitled[pidsInOrder[i]], budget);
        entitled[pidsInOrder[i]] = allowed;
        budget -= allowed;
        if (budget <= 0) budget = 0;
      }
    }

    /* ---- 2. Which variants may we grant or keep? ------------------------
     * `eligible` is the preferred variant plus every fallback the merchant
     * named, mapped to whether it is in stock. The engine NEVER grants a
     * variant outside this set — the automatic discount has to cover each one,
     * and only a human can know that.
     *
     * `cfg.giftVariantId` is the one to grant NOW: the first eligible variant
     * that is actually in stock. /cart/add.js is bulk-atomic, so one dead
     * variant 422s the whole request and would lose the gift on every cart
     * change for the rest of the session — an unavailable variant must never
     * reach the payload.
     *
     * Note the asymmetry, and it is deliberate: we GRANT the preferred
     * variant, but we KEEP a line that is already on any in-stock eligible
     * variant. Otherwise restocking the preferred colour would swap the gift
     * out from under every customer holding a fallback.
     */
    var eligible = {};
    if (Array.isArray(cfg.eligibleVariants) && cfg.eligibleVariants.length) {
      for (var e = 0; e < cfg.eligibleVariants.length; e++) {
        var ev = cfg.eligibleVariants[e];
        var evId = toId(ev && ev.id);
        if (evId) eligible[evId] = ev.available === true;
      }
    } else if (cfg.giftVariantId) {
      eligible[cfg.giftVariantId] = cfg.giftVariantAvailable === true;
    }

    var canGrant = !!(cfg.giftVariantId && cfg.giftVariantAvailable && eligible[cfg.giftVariantId]);

    /* ---- 3. Existing gift lines, grouped by parent ---------------------- */
    var byParent = {};       // parentProductId -> [lines]
    var strays = [];         // gift lines whose parent id is missing/unparseable
    var untagged = [];       // lines of the gift product we did not create

    for (i = 0; i < items.length; i++) {
      item = items[i];
      if (!item || !item.key) continue;

      if (isGiftLine(item, cfg)) {
        var parent = toId(props(item)[P_PARENT]);
        if (!parent) {
          strays.push(item);
        } else {
          if (!byParent[parent]) byParent[parent] = [];
          byParent[parent].push(item);
        }
      } else if (isUntaggedGiftProductLine(item, cfg)) {
        untagged.push(item);
      }
    }

    /* ---- 4. Untagged sponge lines --------------------------------------
     * When standalone purchase is blocked, the reconciler is the backstop
     * that catches every path — including a hand-typed /cart/add URL. This
     * is the layer that actually guarantees it; the PDP and card guards are
     * only UX.
     *
     * When it is NOT blocked, a purchased sponge is left alone and, crucially,
     * does not reduce entitlement: buying a sponge must never cancel the gift.
     */
    if (cfg.blockStandalone) {
      for (i = 0; i < untagged.length; i++) {
        plan.updates[untagged[i].key] = 0;
      }
    }

    /* Gift lines with no usable parent tag are legacy or corrupt. Drop them;
     * a correctly tagged line is added below if entitlement exists. */
    for (i = 0; i < strays.length; i++) {
      plan.updates[strays[i].key] = 0;
    }

    /* ---- 5. Reconcile each parent -------------------------------------- */
    var parentIds = {};
    Object.keys(entitled).forEach(function (k) { parentIds[k] = true; });
    Object.keys(byParent).forEach(function (k) { parentIds[k] = true; });

    Object.keys(parentIds).forEach(function (key) {
      var parentId = toId(key);
      var want = canGrant ? (entitled[parentId] || 0) : 0;
      var lines = byParent[parentId] || [];

      /* Keep at most one line, and only if it is on an eligible variant that
       * is still in stock. Everything else goes to zero — including a line on
       * a variant that has since been removed from the settings, or one whose
       * variant has sold out (it is replaced with an in-stock alternative, or
       * withdrawn if there is none). */
      var keeper = null;
      for (var j = 0; j < lines.length; j++) {
        var line = lines[j];
        if (!keeper && want > 0 && eligible[toId(line.id)] === true) {
          keeper = line;
        } else {
          plan.updates[line.key] = 0;
        }
      }

      if (want <= 0) return;

      if (keeper) {
        if ((toId(keeper.quantity) || 0) !== want) {
          plan.updates[keeper.key] = want;
        }
      } else {
        plan.adds.push({
          id: cfg.giftVariantId,
          quantity: want,
          properties: giftProperties(parentId, parentTitle[parentId])
        });
      }
    });

    return plan;
  }

  /* =====================================================================
   * Everything below is I/O and UI. Skip it outside a browser so the pure
   * core can be unit tested under Node.
   * ===================================================================== */

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      buildPlan: buildPlan,
      planIsEmpty: planIsEmpty,
      isGiftLine: isGiftLine,
      withoutRepeatAdds: withoutRepeatAdds,
      recordAdds: recordAdds
    };
  }

  if (typeof document === 'undefined') return;

  function readConfig() {
    var el = document.getElementById('free-gift-config');
    if (!el) return null;
    var raw;
    try {
      raw = JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
    if (!raw || typeof raw !== 'object') return null;

    return {
      enabled: raw.enabled === true,
      giftProductId: toId(raw.giftProductId) || 0,
      giftVariantId: toId(raw.giftVariantId) || 0,
      giftVariantAvailable: raw.giftVariantAvailable === true,
      eligibleVariants: Array.isArray(raw.eligibleVariants) ? raw.eligibleVariants : [],
      qualifyingProductIds: Array.isArray(raw.qualifyingProductIds) ? raw.qualifyingProductIds : [],
      perUnit: Math.max(1, toId(raw.perUnit) || 1),
      maxPerCart: Math.max(0, toId(raw.maxPerCart) || 0),
      blockStandalone: raw.blockStandalone === true
    };
  }

  var CFG = readConfig();
  if (!CFG) return;

  var DEBOUNCE_MS = 120;

  var engine = {
    /* A reconcile is in flight. Further triggers set `pending` and run once
     * it finishes, rather than overlapping — two cycles reading the cart
     * before either writes is how duplicate gifts happen. */
    busy: false,
    pending: false,
    timer: null
  };

  function miniCart() {
    return document.querySelector('mini-cart');
  }

  /**
   * Sections to re-render. The drawer set comes from <mini-cart>; the /cart
   * page sections are added when present, keyed on the section's data-id
   * (which differs from the element id) — otherwise /cart mutates underneath
   * the customer and goes stale.
   */
  function sectionsToRender() {
    var out = [];
    var mc = miniCart();
    if (mc && typeof mc.getSectionsToRender === 'function') {
      mc.getSectionsToRender().forEach(function (s) {
        out.push({ id: s.id, section: s.section || s.id, selector: s.selector });
      });
    }
    /* The /cart page. Keyed on the element's data-id, which holds the SECTION
     * id ("cart-items" / "cart-footer") and differs from the element id.
     * The selector is `.js-contents`, matching CartItems.getSectionsToRender
     * in cart.js — not `.shopify-section`.
     *
     * In the drawer, #main-cart-items resolves to <div class="mini-cart__main">
     * which carries no data-id, so the guard below correctly skips it. */
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el && el.dataset && el.dataset.id) {
        out.push({ id: id, section: el.dataset.id, selector: '.js-contents' });
      }
    });
    if (document.getElementById('cart-live-region-text')) {
      out.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
    }
    return out;
  }

  function sectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector || '.shopify-section');
    return node ? node.innerHTML : null;
  }

  /**
   * Re-render without calling MiniCart.renderContents(), because that calls
   * open() and would pop the drawer open on every page load.
   */
  function renderSections(state, targets) {
    if (!state || !state.sections) return;
    targets.forEach(function (target) {
      var html = state.sections[target.section] || state.sections[target.id];
      if (!html) return;
      var host = document.getElementById(target.id);
      if (!host) return;
      /* Same shape as CartItems.updateQuantity in cart.js: replace the inner
       * of the matching child when there is one (.js-contents on /cart), and
       * the host itself otherwise (the drawer sections). */
      var elementToReplace = host.querySelector(target.selector) || host;
      var inner = sectionInnerHTML(html, target.selector);
      if (inner !== null) elementToReplace.innerHTML = inner;
    });

    /* is-empty is a class the cart page and drawer both key layout off. */
    if (typeof state.item_count === 'number') {
      var footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', state.item_count === 0);
      var items = document.querySelector('cart-items');
      if (items) items.classList.toggle('is-empty', state.item_count === 0);
    }
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (json) {
        return { ok: res.ok, status: res.status, json: json };
      });
    });
  }

  /* THE CART READ MUST NEVER COME FROM CACHE.
   *
   * This is a GET, and /cart.js is served with headers some browsers will
   * happily satisfy from the memory or disk cache — Safari and several mobile
   * browsers in particular. A cached body is a snapshot from BEFORE the gift
   * we just added, so the reconciler computes "needs 1 sponge" a second time
   * and adds a duplicate. That is the intermittent, device-specific extra
   * sponge: nothing is wrong with the planning logic, the input to it is stale.
   *
   * `cache: 'no-store'` is the fetch-level control; the header is belt and
   * braces for older engines that ignore the option. */
  function getCart() {
    var root = window.theme && theme.routes ? theme.routes.root_url || '/' : '/';
    return fetch(root.replace(/\/$/, '') + '/cart.js', {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' }
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js ' + res.status);
      return res.json();
    });
  }

  /* --- checkout gating -------------------------------------------------
   * US checkout is a plain non-AJAX POST to /cart. None of the GoKwik
   * machinery from the India build applies. All we need is: while a
   * non-empty plan is being applied, the customer must not start checkout
   * with a half-applied gift.
   * ------------------------------------------------------------------- */
  var CHECKOUT_SELECTOR = '[name="checkout"], .shopify-payment-button__button, .cart__checkout-button';
  var checkoutArmed = false;
  var queuedCheckout = null;

  function onCheckoutClick(event) {
    if (!engine.busy) return;
    var trigger = event.target.closest ? event.target.closest(CHECKOUT_SELECTOR) : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    queuedCheckout = trigger;
  }

  function armCheckoutGuard() {
    if (checkoutArmed) return;
    checkoutArmed = true;
    /* Capture phase so we win before any submit handler. */
    document.addEventListener('click', onCheckoutClick, true);
  }

  function releaseCheckoutGuard() {
    document.body.classList.remove('free-gift-syncing');
    if (!queuedCheckout) return;
    var trigger = queuedCheckout;
    queuedCheckout = null;
    /* The node may have been replaced by the section re-render. */
    var live = document.contains(trigger) ? trigger : document.querySelector(CHECKOUT_SELECTOR);
    if (live) live.click();
  }

  /* --- reconcile ------------------------------------------------------- */

  function applyPlan(plan) {
    var targets = sectionsToRender();
    var sectionIds = targets.map(function (t) { return t.section; });
    var sectionsUrl = window.location.pathname;
    var lastState = null;

    var chain = Promise.resolve();

    if (Object.keys(plan.updates).length) {
      chain = chain.then(function () {
        return postJSON('/cart/update.js', {
          updates: plan.updates,
          sections: sectionIds,
          sections_url: sectionsUrl
        }).then(function (res) {
          if (res.ok) lastState = res.json;
          return res;
        });
      });
    }

    if (plan.adds.length) {
      chain = chain.then(function () {
        return postJSON('/cart/add.js', {
          items: plan.adds,
          sections: sectionIds,
          sections_url: sectionsUrl
        }).then(function (res) {
          if (res.ok) lastState = res.json;
          return res;
        });
      });
    }

    return chain.then(function () {
      /* Render whatever we last got back, so a failed add still shows the
       * successful removal. */
      if (lastState) renderSections(lastState, targets);
    });
  }

  /* Read -> plan -> write -> READ BACK AND VERIFY.
   *
   * /cart/add.js does not return the cart (it returns the lines it added), so
   * the only way to confirm the write landed as intended is to read again.
   * That verification pass is also what corrects a surplus created outside
   * this tab — a second tab, or a stale read — because buildPlan emits a
   * quantity correction for a gift line that does not match entitlement.
   *
   * Bounded, because an unbounded loop against a cart that never converges
   * would hammer the store. buildPlan is proven to converge in one pass by the
   * fuzz tests, so needing more than two is a bug worth logging, not retrying. */
  var MAX_PASSES = 3;

  function reconcile() {
    if (engine.busy) {
      engine.pending = true;
      return Promise.resolve();
    }
    engine.busy = true;

    var addedParents = {};
    var showedSyncing = false;

    function pass(n) {
      return getCart().then(function (cart) {
        var plan = buildPlan(cart, CFG);

        /* Never add twice for the same parent in one cycle, whatever the
         * read said. */
        plan = withoutRepeatAdds(plan, addedParents);
        if (planIsEmpty(plan)) return null;

        if (n >= MAX_PASSES) {
          if (window.console && console.warn) {
            console.warn('[free-gift] not settled after ' + MAX_PASSES + ' passes; leaving the cart alone', plan);
          }
          return null;
        }

        /* Only show the visible syncing state once we know there is work —
         * otherwise every page load dims the checkout button on carts that
         * were never getting a gift. The invisible click guard is already
         * armed; that is the race being closed. */
        if (!showedSyncing) {
          showedSyncing = true;
          document.body.classList.add('free-gift-syncing');
        }

        recordAdds(plan, addedParents);
        return applyPlan(plan).then(function () {
          return pass(n + 1);
        });
      });
    }

    return pass(0)
      .catch(function (err) {
        if (window.console && console.warn) console.warn('[free-gift] reconcile failed', err);
      })
      .then(function () {
        engine.busy = false;
        releaseCheckoutGuard();
        if (engine.pending) {
          engine.pending = false;
          schedule();
        }
      });
  }

  /* No self-echo suppression here on purpose.
   *
   * The previous version skipped scheduling entirely while the engine was
   * mid-write, which silently DROPPED any real customer action that landed in
   * that window. `busy` already prevents overlapping reconciles by queueing,
   * and the engine's own writes dispatch no cart events, so there is nothing
   * to suppress — only work to lose. */
  function schedule() {
    clearTimeout(engine.timer);
    engine.timer = setTimeout(reconcile, DEBOUNCE_MS);
  }

  /* --- triggers --------------------------------------------------------
   * Enumerated from the US theme rather than copied from India:
   *   PDP add-to-cart      -> publish(PUB_SUB_EVENTS.cartUpdate)  product-form.js:71
   *   card quick-add       -> ajaxProduct:added                   global.js:1513
   *   bundle / bento add   -> ajaxProduct:added                   global.js:2655
   *   cart remove          -> ajaxProduct:removed                 cart.js:133/185/385
   *   quantity change      -> cart:updated + cartUpdate           cart.js:329/334
   *   colour-swatch add    -> (nothing) — caught by the drawer observer below
   * ------------------------------------------------------------------- */
  /* Neutralise the superseded adder.
   *
   * `free2Sponges()` in assets/global.js is the old add-only routine. Its call
   * sites are commented out in the current global.js, but a theme can end up
   * live with an OLDER global.js — Shopify's GitHub sync pushes theme-editor
   * state back, and a theme can be published from an earlier state. When that
   * happens the two engines compete:
   *
   *   this engine adds the configured variant, tagged;
   *   free2Sponges() adds its own hardcoded variant, untagged;
   *   this engine then deletes the untagged one as a standalone sponge.
   *
   * The customer sees a gift appear, a second one appear, and one vanish — and
   * if the deletion does not land before they check out, they keep the extra.
   *
   * Overwriting the method is harmless on the current build (nothing calls it)
   * and decisive on an older one. The class is declared at the top level of a
   * classic script, so it lives in the global lexical scope: reachable by bare
   * identifier, not as a property of window.
   */
  function neutraliseLegacyAdder() {
    try {
      if (typeof ComboOfferCartSync === 'undefined') return false;
      var proto = ComboOfferCartSync.prototype;
      if (!proto || typeof proto.free2Sponges !== 'function') return false;
      proto.free2Sponges = function () { return Promise.resolve(); };
      return true;
    } catch (e) {
      return false;
    }
  }

  function bind() {
    /* Only when this engine is actually running the offer. If the feature is
     * switched off in settings, leave whatever else is there alone rather than
     * silently removing all gifting. */
    if (CFG.enabled) {
      window.__freeGift = window.__freeGift || {};
      window.__freeGift.legacyAdderNeutralised = neutraliseLegacyAdder();
    }

    armCheckoutGuard();

    document.addEventListener('cart:updated', schedule);
    document.addEventListener('ajaxProduct:added', schedule);
    document.addEventListener('ajaxProduct:removed', schedule);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
    }

    /* Backstop for paths that emit nothing at all. */
    var drawer = document.querySelector('details.cart-drawer-container');
    if (drawer && typeof MutationObserver !== 'undefined') {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].type === 'attributes' && mutations[i].attributeName === 'class') {
            schedule();
            return;
          }
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }

    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
